import { z } from 'zod';

export const ChatDetailsMetadataSchema = z.object({
  scopeType: z.enum(['PAGE', 'ENTITY']).nullish(),
  scopePath: z.string().nullish(),
  entityType: z.enum(['suggestion']).nullish(),
  entityId: z.string().nullish(),
});

export const ChatDetailsQuerySchema = ChatDetailsMetadataSchema.extend({
  searchQuery: z.string().nullish(),
  chatType: z.enum(['live', 'scheduled']).optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export const ChatItemSchema = ChatDetailsMetadataSchema.extend({
  chatSessionId: z.string(),
  chatTitle: z.string().nullable(),
  updatedAt: z.string(),
  createdAt: z.string(),
  type: z.enum(['live', 'scheduled']),
  status: z.enum(['active', 'scheduled', 'expired']).nullable(),
});

export const GraphTypeSchema = z.enum([
  'barchart',
  'lineplot',
  'piechart',
  'scatterplot',
]);

export const GraphSchema = z.object({
  graphType: GraphTypeSchema,
  instructions: z.object({
    x: z.string(),
    y: z.array(z.string()),
    label: z.string().nullish(),
  }),
});

const AiTableInstructionSchema = z
  .union([z.number(), z.record(z.string(), z.number())])
  .optional();

const AiMetadataSchema = z.object({
  error: z.string().nullish(),
  translatedUserQuery: z.string().nullish(),
  isNumerical: z.boolean().nullish(),
  queryId: z.string().optional(),
  query: z.string().nullish(),
  isApplicable: z.boolean().nullish(), // Removed in PDT-100987
  reasoningGroupId: z.string().optional(),
  updateUi: z.boolean().optional(),
  webSearchSources: z.array(z.string()).optional(),
  knowledgeBaseSources: z.array(z.string()).optional(),
  nCols: AiTableInstructionSchema,
  nRows: AiTableInstructionSchema,
  graph: z.union([z.array(z.unknown()), GraphSchema, z.object({})]).optional(), // Deprecated key, old implementation for tables and charts. Use `graphs` key instead.
  /* Report download. Two shapes are on the wire at the same time: the legacy one
     keeps `toDownload` / `pdfRequired` / `pdfS3Path` flat on this object and
     carries only `{ s3Path, url }` under `report`; the current one nests all of
     them inside `report` and adds `uri`. Messages of both shapes coexist inside
     a single chat history, so every field here is optional — `resolveReportDownload`
     in @mint-lib/ai-chat is the single reader that reconciles them.
     Once no legacy payloads remain, delete the three flat keys
     below plus `report.url`, and make the remaining `report` fields required.
     `objectS3Path` goes with them only if the backend also fills `report.s3Path`
     for data_analyst exports — confirm before dropping it. */

  //START: Old report fields. Remove these fields next 2027 spring or summer (this is a tentative deadline)
  toDownload: z.boolean().nullish(),
  pdfRequired: z.boolean().nullish(),
  pdfS3Path: z.string().nullish(),
  //END: Old report fields

  //New report object
  report: z
    .object({
      toDownload: z.boolean().nullish(),
      uri: z.string().nullish(),
      s3Path: z.string().nullish(),
      pdfRequired: z.boolean().nullish(),
      pdfS3Path: z.string().nullish(),
      url: z.string().nullish(),
    })
    .nullish(),
  // mint-iq additive fields (read off the nested metadata bag)
  objectS3Path: z.string().nullish(),
  // Start ImportHub reads these import-domain payloads (MappingDict / PerformanceOutput
  mediaplan: z.unknown().optional(),
  performance: z.unknown().optional(),
  state: z.unknown().optional(),
  // End ImportHUb
});

export const UserMetadataSchema = z
  .object({
    targetFiles: z.array(z.string()).optional(),
    reco: z
      .object({
        recoId: z.number(),
      })
      .optional(),
  })
  .loose()
  .optional();

export const DataframeSchema = z.array(
  z.record(
    z.string(),
    z.union([z.string(), z.number(), z.boolean(), z.null()]),
  ),
);

export const AiMessageContentSchema = z.object({
  content: z.string(),
  type: z.literal('ai'),
  additionalMetadata: z.object({
    isLast: z.boolean().optional(),
    scheduled: z.boolean().optional(),
    /* eventType defines the subtype of an AI message. There are three possible values:
       Reasoning — Represents the AI’s internal thought process while forming a response. It helps the system (or UI) show that the AI is “thinking” or working through a problem.
       Action — Indicates that the AI is performing or describing a specific operation (for example, searching data, calling an API, or executing a command).
       Message — A standard chat message visible to the user. This is usually sent after any reasoning or action steps are complete.
     */
    eventType: z.enum(['action', 'message', 'reasoning']).optional(),
    dataframe: DataframeSchema.optional(), // Deprecated key, old implementation for tables and charts. Use `tables` instead.
    agentName: z.string().optional(),
    currentContext: z.string().optional(),
    metadata: AiMetadataSchema.optional(),
    statusCode: z.number().optional(),
    tables: z.record(z.string(), DataframeSchema).nullish(), // Dataframes for Multiple Charts&Tables
    graphs: z.record(z.string(), GraphSchema).nullish(), // Chart instructions for Multiple Charts&Tables

    // Start mint-iq additive fields (read at the top level of additionalMetadata).
    s3Path: z.string().nullish(),
    attachment: z.string().nullish(),
    attachments: z.array(z.string()).nullish(),
    files: z.array(z.string()).nullish(),
    // End mint-iq additive fields
    // Top-level fallback for the chart spec (extractGraph reads `metadata.graph`
    // first, then `additionalMetadata.graph`).
    graph: GraphSchema.optional(),
  }),
  isSystemError: z.boolean().prefault(false).optional(),
});

const UserMessageContentSchema = z.object({
  content: z.string(),
  type: z.literal('human'),
  additionalMetadata: z.object({
    scheduled: z.boolean().optional(),
    metadata: UserMetadataSchema.optional(),
    /* Message can be cancelled */
    cancelReason: z.string().optional(),
    cancelled: z.boolean().optional(),
    cancelledAt: z.string().optional(),
  }),
  isMsgStub: z.boolean().prefault(false).optional(),
  isApprovingStub: z.boolean().prefault(false).optional(),
});

export const AiChatMessageRecordSchema = z.object({
  createdAt: z.iso.datetime({
    local: true,
  }),
  messageId: z.number(),
  message: AiMessageContentSchema,
});

export const UserChatMessageRecordSchema = z.object({
  createdAt: z.iso.datetime({
    local: true,
  }),
  messageId: z.number(),
  message: UserMessageContentSchema,
});

export const ChatMessageRecordSchema = z.union([
  AiChatMessageRecordSchema,
  UserChatMessageRecordSchema,
]);
